/** IMP-00001 — Master Development Execution Plan (mirrors pkg/masterdevelopment) */

export const IMP_MDEP_ID = 'IMP-00001';

export interface TeamMemberRow {
  role: string;
  responsibility: string;
  decisionRights: string;
  reviewCycle: string;
}

export interface PrincipleRow {
  principle: string;
  status: string;
  score: number;
}

export interface PhaseRow {
  phase: string;
  progress: number;
  status: string;
  wave: number;
}

export interface DependencySprintRow {
  category: string;
  item: string;
  status: string;
  count: number;
}

export interface AIDevGovernanceRow {
  requirement: string;
  status: string;
  score: number;
}

export interface DefinitionRow {
  type: string;
  item: string;
  status: string;
  score: number;
}

export interface QualityGateRow {
  gate: string;
  status: string;
  score: number;
}

export interface ExecutiveDashboardRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const TEAM_ROLES = [
  'Chief Executive Officer', 'Chief Product Officer', 'Chief Technology Officer', 'Chief Enterprise Architect',
  'AI Architect', 'Engineering Manager', 'Backend Engineering Lead', 'Frontend Engineering Lead',
  'Database Architect', 'DevOps Lead',
];

export const EXTENDED_TEAM = ['SRE Lead', 'QA Lead', 'UX Lead', 'Security Lead', 'Product Owners', 'Scrum Masters', 'AI Coding Agents', 'Human Developers'];

export const EXECUTION_PRINCIPLES = [
  'Business First', 'Architecture First', 'Database First', 'API First', 'Event Driven', 'AI Native',
  'Security by Design', 'Test Driven', 'Documentation Driven',
];

export const CONTINUOUS_PRACTICES = ['Continuous Integration', 'Continuous Validation', 'Continuous Review', 'Continuous Improvement'];

export const IMPLEMENTATION_PHASES = [
  'Phase 0: Foundation', 'Phase 1: Core Platform', 'Phase 2: Commerce Platform', 'Phase 3: Marketplace Platform',
  'Phase 4: AI Platform', 'Phase 5: Content Platform', 'Phase 6: Advertising Platform', 'Phase 7: Finance Platform',
  'Phase 8: Analytics Platform', 'Phase 9: Automation Platform', 'Phase 10: Customer Success', 'Phase 11: Stabilization',
  'Phase 12: Internal Alpha', 'Phase 13: Customer Beta', 'Phase 14: General Availability',
];

export const WAVE_REQUIREMENTS = ['Business Goals', 'Technical Goals', 'Acceptance Criteria', 'Dependencies', 'Definition of Done', 'Risk Assessment'];

export const DEPENDENCY_MANAGEMENT = ['Dependencies', 'Blocking Items', 'Parallel Work', 'Integration Points', 'Owner', 'Completion Status'];

export const SPRINT_MANAGEMENT = ['Objectives', 'Stories', 'Tasks', 'Risks', 'Dependencies', 'Acceptance Criteria', 'Review', 'Retrospective'];

export const AI_DEV_GOVERNANCE = [
  'Prompt ID', 'Prompt Version', 'AI Model', 'Generated Timestamp', 'Reviewer',
  'Architecture Compliance', 'Security Validation', 'Performance Validation', 'Approval',
];

export const DEFINITION_OF_READY = ['Requirements Approved', 'Architecture Approved', 'Dependencies Resolved', 'Acceptance Criteria Defined', 'Design Complete'];

export const DEFINITION_OF_DONE = [
  'Backend Complete', 'Frontend Complete', 'Database Complete', 'API Complete', 'Events Complete', 'AI Complete',
  'Tests Passing', 'Documentation Complete', 'Accessibility Complete', 'Performance Validated', 'Security Approved', 'Product Approved',
];

export const QUALITY_GATES = [
  'Static Analysis', 'Code Review', 'Unit Tests', 'Integration Tests', 'Marketplace Tests',
  'AI Validation', 'Performance Tests', 'Accessibility', 'Security Scan',
];

export const TEAM_RESPONSIBILITIES = ['CEO', 'CTO', 'Architect', 'Backend', 'Frontend', 'Database', 'DevOps', 'QA', 'Security', 'AI', 'UX', 'Support', 'Documentation'];

export const RISK_MANAGEMENT = ['Risk Register', 'Mitigation Plans', 'Escalation Matrix', 'Recovery Plans', 'Contingency Plans'];

export const REPORTING = ['Daily Reports', 'Sprint Reports', 'Release Reports', 'Risk Reports', 'Velocity', 'Burndown', 'Quality', 'Architecture Compliance'];

export const SUCCESS_METRICS = ['Velocity', 'Lead Time', 'Cycle Time', 'Deployment Frequency', 'Bug Density', 'Coverage', 'Performance', 'Customer Readiness', 'Business Readiness'];

export const EXECUTIVE_DASHBOARD = ['Overall Completion', 'Module Completion', 'Critical Path', 'Risks', 'Quality Score', 'Budget', 'Timeline', 'Release Readiness'];

export const ARCHITECTURE_STAGES = [
  'PMO Team', 'Execution Principles', 'Implementation Phases', 'Dependency & Sprint',
  'AI Dev Governance', 'Definition Ready/Done', 'Quality Gates', 'Executive Dashboard',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterConstitution: '/platform/master-constitution',
  engineeringGovernance: '/platform/engineering-governance',
  releaseGovernance: '/platform/release-governance',
  enterpriseGA: '/platform/enterprise-ga',
};

export const DEMO_TEAM: TeamMemberRow[] = [
  { role: 'Chief Executive Officer', responsibility: 'Business strategy & commercial launch', decisionRights: 'Executive go/no-go', reviewCycle: 'Monthly' },
  { role: 'Chief Technology Officer', responsibility: 'Technical delivery & architecture compliance', decisionRights: 'Technical approval', reviewCycle: 'Weekly' },
  { role: 'Chief Product Officer', responsibility: 'Product roadmap & business value', decisionRights: 'Product priority approval', reviewCycle: 'Weekly' },
  { role: 'Chief Enterprise Architect', responsibility: 'Architecture integrity & ADR governance', decisionRights: 'Architecture gate veto', reviewCycle: 'Bi-weekly' },
  { role: 'Engineering Manager', responsibility: 'Sprint execution & team allocation', decisionRights: 'Sprint commitment', reviewCycle: 'Weekly' },
  { role: 'DevOps Lead', responsibility: 'CI/CD, environments & release pipeline', decisionRights: 'Deployment approval', reviewCycle: 'Weekly' },
];

export const DEMO_PRINCIPLES: PrincipleRow[] = [
  { principle: 'Business First', status: 'enforced', score: 98 },
  { principle: 'Architecture First', status: 'enforced', score: 97 },
  { principle: 'Database First', status: 'enforced', score: 95 },
  { principle: 'API First', status: 'enforced', score: 96 },
  { principle: 'Event Driven', status: 'active', score: 92 },
  { principle: 'AI Native', status: 'active', score: 90 },
  { principle: 'Security by Design', status: 'enforced', score: 97 },
  { principle: 'Test Driven', status: 'enforced', score: 94 },
];

export const DEMO_PHASES: PhaseRow[] = [
  { phase: 'Phase 0: Foundation', progress: 100, status: 'complete', wave: 0 },
  { phase: 'Phase 1: Core Platform', progress: 85, status: 'active', wave: 1 },
  { phase: 'Phase 2: Commerce Platform', progress: 72, status: 'active', wave: 2 },
  { phase: 'Phase 3: Marketplace Platform', progress: 68, status: 'active', wave: 3 },
  { phase: 'Phase 4: AI Platform', progress: 65, status: 'active', wave: 4 },
  { phase: 'Phase 12: Internal Alpha', progress: 45, status: 'in_progress', wave: 12 },
  { phase: 'Phase 14: General Availability', progress: 28, status: 'planned', wave: 14 },
];

export const DEMO_DEPENDENCY_SPRINT: DependencySprintRow[] = [
  { category: 'Dependencies', item: 'Active module dependencies', status: 'tracked', count: 48 },
  { category: 'Dependencies', item: 'Blocking items', status: 'open', count: 6 },
  { category: 'Sprint', item: 'Current sprint stories', status: 'in_progress', count: 34 },
  { category: 'Sprint', item: 'Sprint velocity (avg)', status: 'stable', count: 42 },
  { category: 'Integration', item: 'Integration points', status: 'mapped', count: 86 },
];

export const DEMO_AI_DEV: AIDevGovernanceRow[] = [
  { requirement: 'Prompt ID & Version Tracked', status: 'required', score: 100 },
  { requirement: 'AI Model Recorded', status: 'required', score: 98 },
  { requirement: 'Human Reviewer Assigned', status: 'required', score: 96 },
  { requirement: 'Architecture Compliance', status: 'required', score: 95 },
  { requirement: 'Security Validation', status: 'required', score: 97 },
];

export const DEMO_DEFINITION: DefinitionRow[] = [
  { type: 'DoR', item: 'Requirements Approved', status: 'gate', score: 100 },
  { type: 'DoR', item: 'Architecture Approved', status: 'gate', score: 98 },
  { type: 'DoR', item: 'Dependencies Resolved', status: 'gate', score: 95 },
  { type: 'DoD', item: 'Tests Passing', status: 'gate', score: 96 },
  { type: 'DoD', item: 'Security Approved', status: 'gate', score: 97 },
  { type: 'DoD', item: 'Product Approved', status: 'gate', score: 94 },
];

export const DEMO_QUALITY_GATES: QualityGateRow[] = [
  { gate: 'Static Analysis', status: 'passing', score: 100 },
  { gate: 'Code Review', status: 'passing', score: 98 },
  { gate: 'Unit Tests', status: 'passing', score: 94 },
  { gate: 'Integration Tests', status: 'passing', score: 90 },
  { gate: 'AI Validation', status: 'passing', score: 88 },
  { gate: 'Security Scan', status: 'passing', score: 97 },
];

export const DEMO_EXECUTIVE: ExecutiveDashboardRow[] = [
  { metric: 'Overall Completion', value: '62%', status: 'on_track', score: 62 },
  { metric: 'Critical Path', value: 'Phase 4 AI Platform', status: 'active', score: 65 },
  { metric: 'Quality Score', value: 'A-', status: 'healthy', score: 91 },
  { metric: 'Velocity', value: '42 pts/sprint', status: 'stable', score: 88 },
  { metric: 'Release Readiness', value: 'Alpha prep', status: 'in_progress', score: 45 },
  { metric: 'Business Readiness', value: 'Beta planning', status: 'planned', score: 35 },
];

export function buildMDEPDashboard() {
  return {
    team_roles: TEAM_ROLES.length,
    execution_principles: EXECUTION_PRINCIPLES.length,
    implementation_phases: IMPLEMENTATION_PHASES.length,
    quality_gates: QUALITY_GATES.length,
    governance_areas: DEPENDENCY_MANAGEMENT.length + AI_DEV_GOVERNANCE.length,
  };
}
